"use client";

import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { m } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Heart, Home, MessageCircle } from 'lucide-react';

export default function AboutClient() {
    return (
        <div className="flex flex-col min-h-screen bg-background">
            <Header />
            <main className="flex-grow pt-32 pb-24">
                <div className="container mx-auto px-6 max-w-5xl">
                    <m.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <div className="text-center md:text-left mb-16">
                            <Badge variant="secondary" className="mb-6 px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] font-black">
                                Our Story
                            </Badge>
                            <h1 className="font-headline text-5xl md:text-7xl font-black text-foreground mb-12 tracking-tight leading-[1.1]">
                                Healthy, happy — <br />
                                <span className="text-primary italic font-normal">your dream puppy</span>
                            </h1>

                            <p className="text-2xl text-foreground font-medium max-w-3xl leading-relaxed">
                                Bay Valley Pups is based in <span className="text-primary font-bold">Roseville, California</span> and focused on connecting loving families with well-cared-for Toy Poodle and Toy Maltipoo puppies.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-12 gap-10">
                            <div className="md:col-span-8 space-y-10">
                                <Card className="bg-secondary/20 border-none shadow-none rounded-[2.5rem] p-4">
                                    <CardContent className="p-8 md:p-10">
                                        <h2 className="text-2xl font-headline font-bold text-foreground mb-4">Established in 2020</h2>
                                        <p className="text-lg text-muted-foreground leading-relaxed font-medium">
                                            We began our journey with a commitment to responsible care and thoughtful placement. We keep our program intentionally small so each puppy receives individual attention, handling, and early socialization.
                                        </p>
                                    </CardContent>
                                </Card>

                                <div>
                                    <h2 className="text-3xl font-headline font-bold text-foreground mb-8">Our approach</h2>
                                    <div className="grid gap-6 mb-12">
                                        {[
                                            {
                                                icon: Home,
                                                text: "Home environment with daily interaction",
                                                desc: "Our puppies are never kept in kennels. They are raised right in our living room, exposed to daily household sounds, children, and other pets to ensure they are confident and well-adjusted."
                                            },
                                            {
                                                icon: Heart,
                                                text: "Focus on temperament and companionship",
                                                desc: "We prioritize temperament above all else. We focus specifically on gentle, affectionate natures that make perfect family companions and therapy dog candidates."
                                            },
                                            {
                                                icon: MessageCircle,
                                                text: "Clear communication and support for new owners",
                                                desc: "We believe in building relationships, not just selling puppies. We provide lifetime support for all our families, answering questions about training, grooming, and health long after you take your puppy home."
                                            }
                                        ].map((item, i) => (
                                            <Card key={i} className="border-border shadow-sm hover:shadow-md transition-all duration-300">
                                                <CardContent className="p-6">
                                                    <div className="flex items-center gap-6 mb-4">
                                                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                                                            <item.icon className="w-6 h-6" />
                                                        </div>
                                                        <span className="text-lg font-bold text-foreground">{item.text}</span>
                                                    </div>
                                                    <p className="text-muted-foreground leading-relaxed pl-[4.5rem]">
                                                        {item.desc}
                                                    </p>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>

                                    <div className="prose prose-lg prose-stone max-w-none text-muted-foreground">
                                        <h2 className="text-3xl font-headline font-bold text-foreground mb-6">Our Philosophy</h2>
                                        <p className="leading-relaxed mb-6">
                                            At Bay Valley Pups, we believe that an ethical program is built on three pillars: <strong>Health, Temperament, and Socialization</strong>. We are not just finding homes for pets; we are preparing future family members. Every decision we make, from the selection of each puppy to the nutrition we provide, is centered around their well-being.
                                        </p>
                                        <p className="leading-relaxed mb-6">
                                            We meticulously screen every puppy for health and temperament. This includes ensuring they meet our strict standards for physical soundness and genetic health. By prioritizing these factors, we give our puppies the best possible start in life.
                                        </p>
                                        <h3 className="text-2xl font-headline font-bold text-foreground mb-4">Why Socialization Matters</h3>
                                        <p className="leading-relaxed">
                                            The first few months of a puppy's life are critical for development. We utilize <strong>Early Neurological Stimulation (ENS)</strong> and <strong>Puppy Culture</strong> protocols to help our puppies develop resilience and adaptability. They are handled daily, introduced to new textures, sounds, and experiences, ensuring they transition smoothly into your home and life.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="md:col-span-4">
                                <div className="sticky top-32">
                                    <Card className="bg-card shadow-soft rounded-[2rem] border-border/50 overflow-hidden">
                                        <div className="h-2 bg-primary w-full" />
                                        <CardContent className="p-8">
                                            <h3 className="text-xl font-bold text-foreground mb-4">Buy with confidence</h3>
                                            <p className="text-muted-foreground mb-6 leading-relaxed">
                                                Buying a puppy online can feel uncertain. We encourage questions and schedule visits by appointment.
                                            </p>
                                            <div className="space-y-4">
                                                <div className="flex items-center gap-3 text-sm font-medium text-foreground">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                                                    Video calls available
                                                </div>
                                                <div className="flex items-center gap-3 text-sm font-medium text-foreground">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                                                    Vet checked parents
                                                </div>
                                                <div className="flex items-center gap-3 text-sm font-medium text-foreground">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                                                    Transparent health history
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>
                        </div>
                    </m.div>
                </div>
            </main>
            <Footer />
        </div>
    );
}
